import { useMemo } from 'react';

import Config from '@config';
import { initializeOrganizationsApi } from '@gasbi/shared/common/features/organizations';
import { useSelector } from 'react-redux';

import { useGetOrganizationRolesInfiniteQuery } from '@/features/roles/organizationRolesApi';
import { cachedItemByIdSelector } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

// BMS's ГРМ card, list and lookups are billing-organizations (GSB-8317). The list stays on BMS: the context
// organization needs its type (head office or not), which the shared RBAC list does not return. The roles are
// Households Billing's own (features/roles/organizationRolesApi, GSB-8308).
const organizations = initializeOrganizationsApi({
  baseUrl: Config.VITE_API_CLIENT_URL,
  baseQuery: axiosBaseQuery,
  organizationsPath: 'billing-organizations',
});

export const {
  organizationsApi,

  useGetReferenceBooksQuery,
  useGetOrganizationsGdnQuery,
  useGetOrganizationByIdQuery,
  useUpdateOrganizationByIdMutation,
  useGetOrganizationDepartmentsQuery,
  useGetOrganizationEmployeePositionsQuery,

  useGetOrganizationsInfiniteQuery,
} = organizations;

export { useGetOrganizationRolesInfiniteQuery };

const getCachedOrganizationByIdSelector = cachedItemByIdSelector(
  'organizationsApi',
  'getOrganizations',
);

export const useGetCachedOrganizationByIdSelector = (id: string) =>
  useSelector(getCachedOrganizationByIdSelector(id));

const getCachedRoleByIdSelector = cachedItemByIdSelector(
  'householdsApi',
  'getOrganizationRoles',
);

export const useGetCachedRoleByIdSelector = (id: string) =>
  useSelector(getCachedRoleByIdSelector(id));

type RolePickerArgs = { organizationId?: string; isFilter?: boolean };

/**
 * The role pickers of the shared organization page (RbacOrganization: employee forms, employees filter) ask
 * roles/filter/query. roles/query needs Organizations.RoleAndPermission.Read, which Households grants nobody (roles are
 * administered in Distribution Admin); the filter endpoint returns the same roles of the organization.
 */
export const useGetRolePickerInfiniteQuery = (
  args?: RolePickerArgs,
  options?: { skip?: boolean },
) =>
  useGetOrganizationRolesInfiniteQuery(
    args ? { ...args, isFilter: true } : undefined,
    options,
  );

/** The roles filter of the shared employees list (RbacOrganization), filtered by name in memory as in Supply. */
export const useGetRolesForFilterQuery = (
  { organizationId, name }: { organizationId?: string; name?: string } = {},
  options?: { skip?: boolean },
) => {
  const result = useGetRolePickerInfiniteQuery(
    organizationId ? { organizationId } : undefined,
    options,
  );

  const data = useMemo(() => {
    const roles = result.data ?? [];
    if (!name) return roles;
    const term = name.toLowerCase();
    return roles.filter(role => role.name.toLowerCase().includes(term));
  }, [result.data, name]);

  return { ...result, data };
};

export { normalizeOrganizationPayloadBeforeMutation } from '@gasbi/shared/common/features/organizations';
